import { useMemo, useState } from 'react';
import { useLookups, useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import { useView } from '@/hooks/useView';
import { dateTime, hours as fmtHours, humanise, relativeDays, tonnes as fmtTonnes } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { EvidenceCount } from '@/components/evidence/Evidence';
import { BreakdownDrawer } from './breakdowns/BreakdownDrawer';
import { WeatherDrawer } from './breakdowns/WeatherDrawer';
import { QUARRY_SITE_ID } from '@/lib/fleet';
import type { Site } from '@/types/models';

interface Breakdown extends Record<string, unknown> {
  id: string;
  breakdown_no: string;
  machine_id: string;
  category_id: string | null;
  severity: string;
  started_at: string;
  ended_at: string | null;
  downtime_hours: number | null;
  breakdown_date: string;
  failure_description: string;
  root_cause: string | null;
  technician: string | null;
  status: string;
  production_impact_t: number | null;
  site_id?: string;
}

interface WeatherEvent extends Record<string, unknown> {
  id: string;
  event_date: string;
  started_at: string;
  ended_at: string | null;
  hours_lost: number | null;
  weather_type_id: string | null;
  affected_area: string | null;
  production_lost_t: number | null;
}

interface MachineStatus {
  machine_id: string;
  fleet_no: string;
  effective_status: string;
  down_since: string | null;
  hours_down: number | null;
  failure_description: string | null;
  breakdown_status: string | null;
  blocking_parts: string | null;
  earliest_eta: string | null;
}

const OPEN = ['reported', 'diagnosed', 'awaiting_parts', 'in_progress'];

const STATUS_TONE: Record<string, 'grey' | 'blue' | 'amber' | 'green' | 'red'> = {
  reported: 'red',
  diagnosed: 'amber',
  awaiting_parts: 'amber',
  in_progress: 'blue',
  repaired: 'green',
  closed: 'grey',
  cancelled: 'grey',
};

const SEVERITY_TONE: Record<string, 'grey' | 'amber' | 'red'> = {
  minor: 'grey', major: 'amber', critical: 'red',
};

export function Breakdowns() {
  const [tab, setTab] = useState<'breakdowns' | 'weather'>('breakdowns');
  const [editing, setEditing] = useState<Partial<Breakdown> | null>(null);
  const [weather, setWeather] = useState<Partial<WeatherEvent> | null>(null);

  const allBreakdowns = useRows<Breakdown>('breakdowns', 'breakdown_date');
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();
  // The quarry by default. The workshop and management follow every site.
  const [site, setSite] = useState<string>(QUARRY_SITE_ID);
  const siteName = (id: string | undefined) => (sites ?? []).find((x) => x.id === (id ?? QUARRY_SITE_ID))?.name ?? '—';
  const breakdowns = useMemo(
    () => allBreakdowns?.filter((b) => site === 'all' || (b.site_id ?? QUARRY_SITE_ID) === site),
    [allBreakdowns, site],
  );
  const evidence = useRows<{ id: string; entity_type: string; entity_id: string }>('attachments');
  const weatherEvents = useRows<WeatherEvent>('weather_events', 'event_date');
  const lookups = useLookups([
    { table: 'machines', label: 'fleet_no' },
    { table: 'breakdown_categories', label: 'name' },
    { table: 'weather_types', label: 'name' },
  ]);
  const { rows: statuses } = useView<MachineStatus & { site_id?: string }>('v_vehicle_status');
  const canEdit = useAuthStore((s) => s.hasRole('maintenance', 'supervisor', 'manager', 'admin'));
  const role = useAuthStore((s) => s.profile?.role);
  // Everyone may open a record; who may change it is said inside (0046).
  const lock = roleLock(role, ['maintenance', 'supervisor', 'manager', 'admin'], 'breakdowns');
  // Weather is a supervisor's record, not maintenance's: the database
  // refuses a maintenance user's save, so the button is not offered.
  const canEditWeather = useAuthStore((s) => s.hasRole('supervisor', 'manager', 'admin'));
  const weatherLock = roleLock(role, ['supervisor', 'manager', 'admin'], 'weather records');

  const down = useMemo(
    () => (statuses ?? []).filter((s) => s.effective_status === 'down'
      && (site === 'all' || (s.site_id ?? QUARRY_SITE_ID) === site)),
    [statuses, site],
  );

  const photoCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of evidence ?? []) {
      if (a.entity_type !== 'breakdown') continue;
      m.set(a.entity_id, (m.get(a.entity_id) ?? 0) + 1);
    }
    return m;
  }, [evidence]);

  const summary = useMemo(() => {
    const list = breakdowns ?? [];
    const open = list.filter((b) => OPEN.includes(b.status));
    return {
      down: down.length,
      open: open.length,
      critical: open.filter((b) => b.severity === 'critical').length,
      awaitingParts: open.filter((b) => b.status === 'awaiting_parts').length,
      // A breakdown that is still open has no downtime_hours yet — counting
      // only closed ones would report 0 h lost while a machine stands idle.
      hoursThisMonth: list
        .filter((b) => b.breakdown_date >= new Date().toISOString().slice(0, 8) + '01')
        .reduce((s, b) => s + (b.downtime_hours != null
          ? Number(b.downtime_hours)
          : OPEN.includes(b.status)
            ? (Date.now() - new Date(b.started_at).getTime()) / 3_600_000
            : 0), 0),
      weatherHours: (weatherEvents ?? [])
        .filter((w) => w.event_date >= new Date().toISOString().slice(0, 8) + '01')
        .reduce((s, w) => s + (Number(w.hours_lost) || 0), 0),
    };
  }, [breakdowns, weatherEvents, down]);

  const breakdownColumns: Column<Breakdown>[] = [
    {
      key: 'breakdown_no', label: 'Ref',
      render: (b) => (
        <span className="row" style={{ gap: 6, alignItems: 'center' }}>
          <strong>{b.breakdown_no}</strong>
          <EvidenceCount count={photoCount.get(b.id) ?? 0} />
        </span>
      ),
    },
    {
      key: 'machine_id', label: 'Machine',
      value: (b) => lookups.machines?.get(b.machine_id) ?? '',
      render: (b) => (
        <>
          {lookups.machines?.get(b.machine_id) ?? '—'}
          {site === 'all' && (b.site_id ?? QUARRY_SITE_ID) !== QUARRY_SITE_ID && (
            <div className="small muted">{siteName(b.site_id)}</div>
          )}
        </>
      ),
    },
    { key: 'started_at', label: 'Started', render: (b) => dateTime(b.started_at) },
    {
      key: 'downtime_hours', label: 'Down', align: 'right',
      value: (b) => b.downtime_hours ?? 9999,
      render: (b) => b.downtime_hours == null
        ? <StatusChip tone="red">Still down</StatusChip>
        : <span className="tabular">{fmtHours(b.downtime_hours)} h</span>,
    },
    {
      key: 'category_id', label: 'Category',
      value: (b) => lookups.breakdown_categories?.get(b.category_id ?? '') ?? '',
      render: (b) => lookups.breakdown_categories?.get(b.category_id ?? '') ?? '—',
    },
    { key: 'failure_description', label: 'Failure' },
    {
      key: 'severity', label: 'Severity',
      render: (b) => <StatusChip tone={SEVERITY_TONE[b.severity] ?? 'grey'}>{humanise(b.severity)}</StatusChip>,
    },
    {
      key: 'status', label: 'Status',
      render: (b) => <StatusChip tone={STATUS_TONE[b.status] ?? 'grey'}>{humanise(b.status)}</StatusChip>,
    },
  ];

  const weatherColumns: Column<WeatherEvent>[] = [
    { key: 'event_date', label: 'Date' },
    {
      key: 'weather_type_id', label: 'Type',
      value: (w) => lookups.weather_types?.get(w.weather_type_id ?? '') ?? '',
      render: (w) => lookups.weather_types?.get(w.weather_type_id ?? '') ?? '—',
    },
    { key: 'started_at', label: 'From', render: (w) => dateTime(w.started_at) },
    { key: 'ended_at', label: 'To', render: (w) => dateTime(w.ended_at) },
    {
      key: 'hours_lost', label: 'Hours lost', align: 'right',
      render: (w) => <span className="tabular">{fmtHours(w.hours_lost)}</span>,
    },
    { key: 'affected_area', label: 'Area' },
    {
      key: 'production_lost_t', label: 'Production lost', align: 'right',
      render: (w) => <span className="tabular">{fmtTonnes(w.production_lost_t)} t</span>,
    },
  ];

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      {down.length > 0 && (
        <Panel title={`Machines down · ${down.length}`}>
          <div className="col">
            {down.map((m) => (
              <div className="issue issue--block" key={m.machine_id} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ minWidth: 110 }}>{m.fleet_no}</strong>
                <span>{m.failure_description ?? 'Fault not recorded'}</span>
                <span className="small">
                  {m.hours_down != null ? `${fmtHours(m.hours_down)} h down` : ''}
                  {m.breakdown_status ? ` · ${humanise(m.breakdown_status)}` : ''}
                </span>
                <span className="spacer" />
                {m.blocking_parts && (
                  <span className="small" style={{ color: 'var(--rag-amber)' }}>
                    {m.blocking_parts}
                    {m.earliest_eta ? ` · ETA ${relativeDays(m.earliest_eta)}` : ' · no ETA'}
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="small muted" style={{ marginBottom: 0 }}>
            A machine is down while it has an unresolved breakdown — the register
            sets that, not a status field someone has to remember to change.
          </p>
        </Panel>
      )}

      <Panel title="Downtime this month">
        <div className="grid grid--stats">
          <StatTile label="Machines down" value={summary.down} rag={summary.down ? 'red' : 'green'} />
          <StatTile label="Open breakdowns" value={summary.open} rag={summary.open ? 'amber' : 'green'} />
          <StatTile label="Critical" value={summary.critical} rag={summary.critical ? 'red' : 'green'} />
          <StatTile label="Awaiting parts" value={summary.awaitingParts} rag={summary.awaitingParts ? 'amber' : 'green'} />
          <StatTile label="Breakdown hours" value={summary.hoursThisMonth} decimals={1}
            sub={summary.down ? 'includes machines still down' : undefined} />
          <StatTile label="Weather hours" value={summary.weatherHours} decimals={1}
            sub="never counted as machine failure" />
        </div>
      </Panel>

      <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <select aria-label="Site" className="concrete-branch-select" value={site}
          onChange={(e) => setSite(e.target.value)}>
          <option value="all">All sites</option>
          {(sites ?? []).filter((x) => (x.kind === 'quarry' || x.kind === 'branch') && siteOk(x.id)).map((x) => (
            <option key={x.id} value={x.id}>{x.name}</option>
          ))}
        </select>
        <Button variant={tab === 'breakdowns' ? 'primary' : 'secondary'} onClick={() => setTab('breakdowns')}>
          Breakdowns
        </Button>
        <Button variant={tab === 'weather' ? 'primary' : 'secondary'} onClick={() => setTab('weather')}>
          Weather
        </Button>
        <span className="spacer" />
        {tab === 'breakdowns'
          ? canEdit && <Button onClick={() => setEditing({})}>+ Log breakdown</Button>
          : canEditWeather && <Button onClick={() => setWeather({})}>+ Log weather</Button>}
      </div>

      {tab === 'breakdowns' ? (
        <Panel title="Breakdown register" flush>
          <DataTable
            rows={breakdowns}
            columns={breakdownColumns}
            rowKey={(b) => b.id}
            onRowClick={(b) => setEditing(b)}
            defaultSort="started_at"
            searchPlaceholder="Search machine, failure, technician…"
            empty="No breakdowns recorded."
          />
        </Panel>
      ) : (
        <Panel title="Weather downtime" flush>
          <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
            Weather is recorded separately from mechanical downtime, and is excluded
            from machine availability by default — a rained-off shift is not a
            machine that failed.
          </div>
          <DataTable
            rows={weatherEvents}
            columns={weatherColumns}
            rowKey={(w) => w.id}
            onRowClick={(w) => setWeather(w)}
            defaultSort="event_date"
            searchPlaceholder="Search weather events…"
            empty="No weather downtime recorded."
          />
        </Panel>
      )}

      {editing && <BreakdownDrawer breakdown={editing} locked={lock} onClose={() => setEditing(null)} />}
      {weather && <WeatherDrawer event={weather} locked={weatherLock} onClose={() => setWeather(null)} />}
    </div>
  );
}
